import {H5} from '@hm/fundamental'
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  LineController,
  PointElement,
  ScriptableLineSegmentContext,
  Tooltip
} from 'chart.js'
import {useMemo} from 'react'
import {Line} from 'react-chartjs-2'
import {useTranslation} from 'react-i18next'

import {MaterialInstallation} from '@refra/modules/refra-planning/types'

export interface InstallationPerformanceChartProps {
  materialInstallation?: MaterialInstallation
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  LineController
)

export const InstallationPerformanceChart = ({
  materialInstallation
}: InstallationPerformanceChartProps) => {
  const {t} = useTranslation()

  const generateLabels = (fromMeter: number, toMeter: number) => {
    const labels: string[] = []
    let meter = fromMeter
    while (meter <= toMeter) {
      labels.push(`${meter.toFixed(1)} m`)
      meter += 0.2
    }
    labels.push(toMeter.toFixed(1) + ' m')
    return labels
  }

  const generateValues = (valuesConfig: {fromMeter: number; toMeter: number; value: number}[]) => {
    const data: number[] = []
    valuesConfig.forEach(({fromMeter, toMeter, value}) => {
      let meter = fromMeter
      while (meter <= toMeter) {
        data.push(value)
        meter = Number((meter + 0.2).toFixed(1))
      }
    })
    return data
  }

  const chartData = useMemo(() => {
    if (!materialInstallation?.performance) return {labels: [], datasets: []}

    const survived = materialInstallation?.performance?.survived?.values ?? []
    const labels = generateLabels(materialInstallation.fromMeter, materialInstallation.toMeter)

    return {
      labels,
      datasets: [
        {
          label: t('refraPlanning.materialInstallations.performanceChartSurvivedLabel'),
          data: generateValues(survived),
          borderColor: '#9333EA',
          backgroundColor: 'rgba(147, 51, 234, 0.15)',
          stepped: true,
          fill: false,
          pointRadius: 0,
          pointHitRadius: 50,
          segment: {
            borderDash: (ctx: ScriptableLineSegmentContext) =>
              ctx.p0.parsed.y === ctx.p1.parsed.y ? [] : [6, 4]
          }
        }
      ]
    }
  }, [materialInstallation, t])

  const options = useMemo(() => {
    const unit = materialInstallation?.performance?.survived?.unit ?? ''

    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {mode: 'index' as const, intersect: false},
      plugins: {
        legend: {display: true, position: 'top' as const},
        tooltip: {enabled: true}
      },
      scales: {
        x: {
          grid: {display: false},
          ticks: {maxRotation: 0}
        },
        y: {
          grid: {color: 'rgba(0,0,0,0.06)'},
          ticks: {
            callback: (value: string | number) => `${value} ${unit}`
          }
        }
      }
    }
  }, [materialInstallation?.performance?.survived?.unit])

  return (
    <div>
      <H5 data-cy="material_modal_performance_chart_title">
        {t('refraPlanning.materialInstallations.performanceChartTitle')}
      </H5>

      <div style={{height: 250}}>
        <Line data={chartData} options={options} />
      </div>
    </div>
  )
}
